import React, { useState, useRef, useEffect } from 'react';
import {
  CalendarDays, ChevronRight, CheckCircle2, Sparkles, Loader2, AlertCircle,
  PenTool, ListTodo, TrendingUp, Flame, Target, AlertTriangle,
} from 'lucide-react';
import { agentChain } from '../../lib/notion-api';
import { CATEGORY_COLOR } from '../../data/agents';

// ── 금액 포맷 (대시보드 내 사용) ──
function fmtW(n) {
  if (!n || n <= 0) return '—';
  return n >= 10000 ? `${(n / 10000).toFixed(n % 10000 === 0 ? 0 : 1)}만원` : `${n.toLocaleString()}원`;
}

// ═══ 일일 브리핑 카드 ═══
const BRIEFING_KEY_PREFIX = 'caio_daily_briefing_';

function DailyBriefingCard({ data }) {
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  const cacheKey = BRIEFING_KEY_PREFIX + todayStr;

  const [expanded, setExpanded] = React.useState(true);
  const [aiSummary, setAiSummary] = React.useState(() => {
    try { const c = JSON.parse(localStorage.getItem(cacheKey) || 'null'); return c?.aiSummary || null; }
    catch { return null; }
  });
  const [loadingAI, setLoadingAI] = React.useState(false);
  const [aiError, setAiError] = React.useState(null);

  // 자동 생성: 페이지 로드 시 캐시 없으면 Notion 브리핑 조회 → 없으면 자동 생성
  const generateRef = React.useRef(null);

  React.useEffect(() => {
    if (aiSummary) return; // 이미 캐시 있음
    let cancelled = false;
    // 1) Cron이 저장한 Notion 브리핑 먼저 확인
    fetch(`/api/briefing?date=${todayStr}`)
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        if (d.briefing) {
          setAiSummary(d.briefing);
          try { localStorage.setItem(cacheKey, JSON.stringify({ aiSummary: d.briefing, generatedAt: new Date().toISOString() })); } catch {}
        } else {
          // 2) 없으면 1.5초 후 자동 생성 (data 로드 대기)
          const timer = setTimeout(() => {
            if (!cancelled && generateRef.current) generateRef.current();
          }, 1500);
          return () => clearTimeout(timer);
        }
      })
      .catch(() => {
        if (!cancelled) {
          const timer = setTimeout(() => {
            if (generateRef.current) generateRef.current();
          }, 1500);
          return () => clearTimeout(timer);
        }
      });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const todos = data?.todos?.recent || [];
  const topTodos = todos
    .filter(t => t.상태 !== '완료')
    .sort((a, b) => {
      const pa = a.우선순위 === '🔴 높음' ? 0 : a.우선순위 === '🟡 보통' ? 1 : 2;
      const pb = b.우선순위 === '🔴 높음' ? 0 : b.우선순위 === '🟡 보통' ? 1 : 2;
      if (pa !== pb) return pa - pb;
      return (a.날짜 || '9999').localeCompare(b.날짜 || '9999');
    })
    .slice(0, 5);

  const yesterday = new Date(today.getTime() - 86400000);
  const yesterdayStr = yesterday.toISOString().split('T')[0];
  const yesterdayDone = todos.filter(t => t.상태 === '완료' && t.날짜 === yesterdayStr).length;

  const thisMonth = today.toISOString().slice(0, 7);
  const ssRevenue = (() => {
    try {
      const orders = JSON.parse(localStorage.getItem('caio_smartstore_orders') || '[]');
      return orders.filter(o => (o.date || '').slice(0, 7) === thisMonth).reduce((s, o) => s + (o.settle || 0), 0);
    } catch { return 0; }
  })();
  const adRevenue = (() => {
    try { const all = JSON.parse(localStorage.getItem('caio_adpost') || '{}'); return (all[thisMonth] || {}).actual || 0; }
    catch { return 0; }
  })();
  const totalRevenue = ssRevenue + adRevenue;
  const target = parseInt(localStorage.getItem('caio_monthly_target') || '5000000', 10);
  const achievePct = target > 0 ? Math.min(100, Math.round((totalRevenue / target) * 100)) : 0;
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const dRemaining = endOfMonth.getDate() - today.getDate();

  const generateBriefing = async () => {
    if (loadingAI || aiSummary) return;
    setLoadingAI(true); setAiError(null);
    try {
      const todoList = topTodos.length > 0
        ? topTodos.map((t, i) => `${i + 1}. [${t.우선순위 === '🔴 높음' ? '긴급' : '보통'}] ${t.이름}${t.카테고리 ? ` (${t.카테고리})` : ''}`).join('\n')
        : '(오늘 등록된 할일 없음)';
      const command = `오늘은 ${today.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'short' })}입니다.\n\n오늘 할일 TOP5:\n${todoList}\n\n이번달 매출 달성률: ${achievePct}% (목표 ${(target / 10000).toFixed(0)}만원, 현재 ${(totalRevenue / 10000).toFixed(1)}만원)\n남은 일수: ${dRemaining}일 / 어제 완료: ${yesterdayDone}건\n\n이 정보를 바탕으로 오늘의 운영 브리핑을 2~3줄로 요약해줘. 가장 중요한 것 하나와 핵심 액션 포인트를 포함해. 짧고 실용적으로.`;
      const res = await agentChain(command);
      const summary = res?.final?.[0]?.content || (typeof res === 'string' ? res : '분석 완료');
      setAiSummary(summary);
      try { localStorage.setItem(cacheKey, JSON.stringify({ aiSummary: summary, generatedAt: new Date().toISOString() })); } catch {}
    } catch (e) {
      setAiError(e.message);
    } finally {
      setLoadingAI(false);
    }
  };
  // generateRef가 항상 최신 generateBriefing을 가리키도록 동기화
  generateRef.current = generateBriefing;

  const dayStr = today.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'short' });
  const barColor = achievePct >= 80 ? 'var(--green)' : achievePct >= 50 ? 'var(--gold)' : 'var(--orange)';

  return (
    <div className="n-card" style={{ marginBottom: 16, background: 'linear-gradient(135deg, var(--bg3) 0%, var(--bg2) 100%)' }}>
      {/* 헤더 */}
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', marginBottom: expanded ? 14 : 0 }}
        onClick={() => setExpanded(v => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && setExpanded(v => !v)}
        aria-expanded={expanded}
      >
        <CalendarDays size={14} style={{ color: 'var(--gold)', flexShrink: 0 }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--txt)', flex: 1 }}>
          오늘의 브리핑
          <span style={{ fontWeight: 400, color: 'var(--txt2)', fontSize: 12, marginLeft: 6 }}>{dayStr}</span>
        </span>
        <span style={{ fontSize: 10, color: 'var(--txt3)', marginRight: 4 }}>D-{dRemaining}</span>
        <ChevronRight size={13} style={{ color: 'var(--txt3)', transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
      </div>

      {expanded && (
        <>
          {/* 월 달성률 바 */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
              <span style={{ fontSize: 11, color: 'var(--txt3)' }}>이번달 목표 달성률</span>
              <span style={{ fontFamily: 'Outfit', fontSize: 13, fontWeight: 700, color: barColor }}>
                {achievePct}%
              </span>
            </div>
            <div style={{ height: 6, background: 'var(--bg)', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${achievePct}%`, background: barColor, borderRadius: 3, transition: 'width 0.8s ease' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, fontSize: 10, color: 'var(--txt3)', fontFamily: 'Outfit', fontVariantNumeric: 'tabular-nums' }}>
              <span>{(totalRevenue / 10000).toFixed(1)}만원</span>
              <span>목표 {(target / 10000).toFixed(0)}만원 · {dRemaining}일 남음</span>
            </div>
          </div>

          {/* 오늘 우선 할일 TOP5 */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 10, color: 'var(--txt3)', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              오늘 우선 할일
            </div>
            {topTodos.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--txt3)', padding: '4px 0' }}>등록된 할일이 없어요</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {topTodos.map((t, i) => (
                  <div key={t.id || i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
                    <span style={{ fontFamily: 'Outfit', fontSize: 10, fontWeight: 700, color: 'var(--txt3)', minWidth: 14, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>{i + 1}</span>
                    {t.우선순위 === '🔴 높음' && <span className="n-priority-dot high" style={{ fontSize: 9 }}>긴급</span>}
                    <span style={{ fontSize: 12, color: 'var(--txt)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.이름}</span>
                    {t.카테고리 && <span style={{ fontSize: 10, color: CATEGORY_COLOR[t.카테고리] || 'var(--txt3)', flexShrink: 0 }}>{t.카테고리}</span>}
                    {t.날짜 && <span style={{ fontSize: 10, color: 'var(--txt3)', fontFamily: 'Outfit', flexShrink: 0 }}>{t.날짜.slice(5)}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 어제 성과 */}
          {yesterdayDone > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, padding: '6px 10px', background: 'rgba(62,207,142,0.08)', borderRadius: 8, border: '1px solid rgba(62,207,142,0.15)' }}>
              <CheckCircle2 size={12} style={{ color: 'var(--green)', flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: 'var(--green)' }}>어제 <b>{yesterdayDone}건</b> 완료했어요</span>
            </div>
          )}

          {/* AI 브리핑 */}
          {!aiSummary && !loadingAI && (
            <button
              onClick={generateBriefing}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)',
                borderRadius: 8, color: 'var(--gold)', fontSize: 12, fontWeight: 600,
                padding: '7px 14px', cursor: 'pointer', width: '100%',
              }}
            >
              <Sparkles size={13} /> AI 브리핑 생성
            </button>
          )}
          {loadingAI && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--txt3)', padding: '4px 0' }}>
              <Loader2 size={12} className="n-spin" /> 대시·현자 에이전트 분석 중…
            </div>
          )}
          {aiError && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--red)' }}>
              <AlertCircle size={11} /> {aiError}
            </div>
          )}
          {aiSummary && (
            <div style={{ padding: '10px 12px', background: 'rgba(201,168,76,0.06)', borderRadius: 8, border: '1px solid rgba(201,168,76,0.2)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                <Sparkles size={11} style={{ color: 'var(--gold)' }} />
                <span style={{ fontSize: 10, color: 'var(--gold)', fontWeight: 600 }}>AI 브리핑</span>
                <button
                  onClick={() => { setAiSummary(null); try { localStorage.removeItem(cacheKey); } catch {} setLoadingAI(false); }}
                  style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--txt3)', cursor: 'pointer', fontSize: 10 }}
                >재생성</button>
              </div>
              <div style={{ fontSize: 12, color: 'var(--txt)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{aiSummary}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ═══ 오늘의 미션 (방향감·추진감) ═══
function TodayMission({ data }) {
  const today = new Date();
  const dayStr = today.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'short' });
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const remaining = endOfMonth.getDate() - today.getDate();
  const thisMonth = today.toISOString().slice(0, 7);

  const ssOrders = (() => { try { return JSON.parse(localStorage.getItem('caio_smartstore_orders') || '[]'); } catch { return []; } })();
  const ssRevenue = ssOrders.filter(o => (o.date || '').slice(0, 7) === thisMonth).reduce((s, o) => s + (o.settle || 0), 0);
  const target = parseInt(localStorage.getItem('caio_monthly_target') || '5000000', 10);
  const gap = Math.max(0, target - ssRevenue);
  const dailyNeeded = remaining > 0 ? Math.ceil(gap / remaining) : 0;
  const achievePct = Math.min(100, Math.round((ssRevenue / target) * 100));

  const todayStr = today.toISOString().split('T')[0];
  const todayTodos = (data?.todos?.recent || []).filter(t => t.날짜 === todayStr && t.상태 !== '완료');
  const urgentTodos = (data?.todos?.recent || []).filter(t => t.우선순위 === '🔴 높음' && t.상태 !== '완료');
  const healthQueue = data?.health?.byStatus?.['크롤링 완료'] || 0;

  const missions = [];
  if (healthQueue > 0)
    missions.push({ icon: PenTool, label: `건강블로그 ${healthQueue}건 대기`, sub: '헬시가 자동 작성합니다', color: 'var(--status-crawl)' });
  if (todayTodos.length > 0)
    missions.push({ icon: ListTodo, label: `오늘 마감 ${todayTodos.length}건`, sub: todayTodos.slice(0, 2).map(t => t.이름).join(' · '), color: 'var(--red)' });
  if (urgentTodos.length > 0 && missions.length < 3)
    missions.push({ icon: AlertCircle, label: `긴급 할일 ${urgentTodos.length}건`, sub: urgentTodos[0]?.이름, color: 'var(--red)' });
  if (gap > 0)
    missions.push({ icon: TrendingUp, label: `하루 ${fmtW(dailyNeeded)}씩 팔면 달성`, sub: `남은 ${remaining}일 · ${fmtW(gap)} 남음 · ${achievePct}% 달성`, color: 'var(--gold)' });
  else
    missions.push({ icon: Flame, label: '이번달 매출 목표 달성!', sub: '다음달 목표를 업데이트하세요', color: 'var(--green)' });

  return (
    <div className="n-mission-card">
      <div className="n-mission-header">
        <Target size={13} />
        <span>오늘의 미션</span>
        <span className="n-mission-date">{dayStr}</span>
        {remaining > 0 && <span className="n-mission-d">D-{remaining}</span>}
      </div>
      <div className="n-mission-list">
        {missions.slice(0, 3).map((m, i) => (
          <div key={i} className="n-mission-item" style={{ '--mc': m.color }}>
            <m.icon size={13} style={{ color: m.color, flexShrink: 0, marginTop: 2 }} />
            <div>
              <div className="n-mission-label">{m.label}</div>
              {m.sub && <div className="n-mission-sub">{m.sub}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export { DailyBriefingCard, TodayMission };
